import { useTranslation } from 'react-i18next';

import type { Site } from '@hotjar/site';
import type { Segment } from '@hotjar/webapp/features/Recordings/types';
import styled from '@emotion/styled';
import { ForwardToZapier } from '@hotjar/webapp/features/Forwarding/ForwardToZapier';
import { tokens, useNotify, Modal, Button } from '@hotjar/hotsauce';
import type { AxiosError } from 'axios';
import { useState } from 'react';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import {
  Forward as ForwardSegmentRecordingsToSlack,
  useSlackForwardingSettings,
} from './ForwardRecordingsToSlack';
import { SegmentName, useSegmentName } from './SegmentName';
import { FormModeOption, useFormMode } from './FormModeOption';
import type { TemporarySegment } from './use-api';
import { useSave as useApi } from './use-api';

// TODO: move it to `@hjds/tokens/sizes` maybe?
const SPACING_BASE = 4;
const INPUT_WIDTH = SPACING_BASE * 100;

const ModeOptionsSeparator = styled.hr`
  margin: ${tokens.spacer.x2} 0;
  border: none;
  border-top: 1px solid ${tokens.color.ui_border_light};
`;

const SecondWidgetWrapper = styled.div`
  margin-top: ${tokens.spacer.x3};
`;

const CreateWrapper = styled.div`
  margin-top: ${tokens.spacer.x2};
  /* NOTE: this value allow to align the left edge of the container with the start of
   * Radio component label
   */
  margin-left: 30px;
`;

const UpdateWrapper = styled.div`
  margin-top: ${tokens.spacer.x3};
  /* NOTE: this value allow to align the left edge of the container with the start of
   * Radio component label
   */
  margin-left: 30px;
`;

export interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSegmentChange: (segment: Segment) => void;
  segment: TemporarySegment;
  currentSite: Site;
  onUpsellRequest: (featureName: string) => void;
  allowModeSelection?: boolean;
}

export const SegmentEditCreate = ({
  isOpen,
  onClose,
  onSegmentChange,
  segment,
  currentSite,
  onUpsellRequest,
  allowModeSelection = true,
}: Props): JSX.Element => {
  const { showToast } = useNotify();
  const { t } = useTranslation();
  const [formMode, setFormMode, canUpdateSegment] = useFormMode(segment.id);
  const [segmentName, setSegmentName] = useSegmentName(segment.id, segment.name ?? '', formMode);
  const [segmentNameError, setSegmentNameError] = useState(null);
  const [forwardingSettings, setForwardingSettings, saveForwardingSettings] =
    useSlackForwardingSettings(currentSite.id, segment.id, formMode);

  const onSuccess = (text: string) => {
    showToast({ text, kind: 'success' });
    setSegmentNameError(null);
  };
  const onError = (text: string) => showToast({ text, kind: 'danger' });
  const onSaveError = (text: string, error: AxiosError): boolean => {
    if (error?.response?.data?.errors?.name) {
      setSegmentNameError(error.response.data.errors.name[0]);
      return true;
    }

    onError(text);
    return false;
  };

  const onSegmentNameChange = (name: string): void => {
    setSegmentName(name);
    setSegmentNameError(null);
  };

  const [handleSave] = useApi({
    currentSiteId: currentSite.id,
    formMode,
    segment,
    segmentName,
    saveForwardingSettings,
    onSegmentChange,
    onClose,
    onError: onSaveError,
    onSuccess,
  });

  const forwardingWidget = (
    <>
      <ForwardSegmentRecordingsToSlack
        settings={forwardingSettings}
        onChange={setForwardingSettings}
        segmentFilters={segment.filters}
        currentSite={currentSite}
        onEnableError={onError}
        onEnableSuccess={onSuccess}
        onUpsellRequest={onUpsellRequest}
      />
      <SecondWidgetWrapper>
        <ForwardToZapier
          siteId={currentSite.id}
          widgetId={segment.id === -1 ? null : segment.id}
          widgetType="recording_segment"
          disabledTooltip={t('common:recordings.segment-edit-create.forward-to-zapier.disabled')}
          title={t('common:recordings.segment-edit-create.forward-to-zapier.title')}
          contextOfUse={ProductType.Analytics}
        />
      </SecondWidgetWrapper>
    </>
  );
  const nameWidget = (
    <SegmentName
      name={segmentName}
      onChange={onSegmentNameChange}
      inputWidth={INPUT_WIDTH}
      errorMessage={segmentNameError}
    />
  );

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      contentLabel={t('common:recordings.segment-edit-create.header')}
    >
      <Modal.Header title={t('common:recordings.segment-edit-create.header')} />

      <Modal.Body>
        {canUpdateSegment && allowModeSelection ? (
          <form>
            <FormModeOption
              onChange={setFormMode}
              currentMode={formMode}
              mode="update"
              label={t('common:recordings.segment-edit-create.override', {
                segmentName: segment.name,
              })}
            >
              <CreateWrapper>{forwardingWidget}</CreateWrapper>
            </FormModeOption>
            <ModeOptionsSeparator />
            <FormModeOption
              onChange={setFormMode}
              currentMode={formMode}
              mode="create"
              label={t('common:recordings.segment-edit-create.create-new')}
            >
              <UpdateWrapper>
                {nameWidget}
                <SecondWidgetWrapper>{forwardingWidget}</SecondWidgetWrapper>
              </UpdateWrapper>
            </FormModeOption>
          </form>
        ) : (
          <>
            {nameWidget}
            <SecondWidgetWrapper>{forwardingWidget}</SecondWidgetWrapper>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={onClose}>
          {t('common:recordings.segment-edit-create.cancel')}
        </Button>
        <Button kind="primary" onClick={handleSave}>
          {t('common:recordings.segment-edit-create.submit')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
